/**
 * forms.js
 * API for handling getting forms data
 */

import axiosInstance from "./axios-instance";

/**
 * @desc Upload users Teiti Kahurangi form data
 * @param {Object} info 
 * @returns 
 */
const teitiKahurangiFormPost = async function (info){
    return await axiosInstance.post("/forms/teitiKahurangiForm", info).then(res => res);
}

/**
 * @desc Get a single Teiti Kahurangi form by its ID
 * @param {string} formId
 */
const getTeitiKahurangiFormById = async function (formId) {
    return await axiosInstance.get(`/forms/teitiKahurangiForm/form/${formId}`, {
        cache: false
    }).then(res => res);
}

/**
 * @desc Uploads a feedback response to the backend
 * @param {*} feedback_object 
 */
const uploadFeedbackResponse = async function (feedback_object) {
    return await axiosInstance.post("/forms/feedback", feedback_object).then(res => res);
}

/**
 * @desc Returns client Teiti Kahurangi form history data as chart object
 */
const getTeitiKahurangiFormDataHistory = async function (clientId) {
    return await axiosInstance.get(`/forms/teitiKahurangiForm/${clientId}/history`, {
        cache: false
    }).then(res => res);
}

/**
 * @desc Returns client Teiti Kahurangi form history data as chart object
 */
const getTeItiKahurangiFormDataCharts = async function (client_id) {
    return await axiosInstance.get('/forms/teitiKahurangiForm/charts', { 
        params: { client_id },
        cache: false
    }).then(res => res);
}

const getTeitiKahurangiFormDataxportCSV = function (user){
    return axiosInstance({
        url: `/forms/teitiKahurangiForm/csv?userId=${user.id}`,
        method: 'GET',
        responseType: 'blob',
        cache: false
    }).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `teitiKahurangiForm-${user.first_name}-${user.last_name}.csv`); //or any other extension
    document.body.appendChild(link);
    link.click();
    })
}

const updateCaseClosureFormData = async () =>
    await axiosInstance.get("/forms/asaCaseClosureForm", {
        cache: false
    }).then(res => res);

/**
 * Retrieve a metadata preview of all past form responses for the given form
 * type and subject.
 * @param {number|string} formTypeId
 * @param {{ source: string, id: number|null }} subject
 * @param {string} [staffId]
 */
const getFormHistory = async function (formTypeId, subject, staffId) {
    const source = subject?.source ?? 'paua';
    const subjectId = subject?.id ?? null;
    const clientEngagementId = subject?.clientEngagementId ?? subject?.client_engagement_id ?? null;
    return await axiosInstance.get(`/app/dynamic-forms/${formTypeId}/responses/${subjectId}`,
        { cache: false, params: { source, staffId, clientEngagementId } })
    .then(res => res);
}

/**
 * Retrieve the specific past form response data by its ID. Response IDs are
 * globally unique, so the subject segment is not significant server-side.
 */
const getPastFormResponse = async function (formTypeId, responseId) {
    return await axiosInstance.get(`/app/dynamic-forms/${formTypeId}/responses/0/${responseId}`,
        { cache: false })
    .then(res => res);
}

/**
 * Retrieve all available dynamic form type previews
 */
const getDynamicFormTypes = () => axiosInstance.get("/app/dynamic-forms", { cache: false }).then(res => res);

/**
 * Retrieve the structure metadata for a specific type of dynamic form
 */
const getDynamicForm = (formTypeId) => axiosInstance.get(`/app/dynamic-forms/${formTypeId}`, { cache: false }).then(res => res);

const getKoTatouResponses = () =>
    axiosInstance.get("/app/ko-tatou/responses", { cache: false }).then(res => res);

/**
 * Build the legacy inline-client payload (name/email/phone) from a subject.
 * Only relevant for the 'legacy_temporary' source; undefined otherwise.
 */
const legacyClientPayload = (subject) => {
    if (subject?.source !== 'legacy_temporary') return undefined;
    return {
        name: subject?.name ?? null,
        email: subject?.email ?? null,
        phone: subject?.phone ?? null,
    };
}

/**
 * Submit a new dynamic form response
 * @param {{ source: string, id: number|null }} subject
 */
const submitDynamicForm = (formTypeId, subject, response, complete=false, status='incomplete', revision=null) => {
    const source = subject?.source ?? 'paua';
    const subjectId = subject?.id ?? null;
    const client_engagement_id = subject?.clientEngagementId ?? subject?.client_engagement_id ?? null;
    return axiosInstance.post(`/app/dynamic-forms/${formTypeId}/responses/${subjectId}`,
        { response, complete, status, revision, source, client: legacyClientPayload(subject), client_engagement_id });
}

/**
 * Update an existing dynamic form response
 * @param {{ source: string, id: number|null }} subject
 */
const updateDynamicForm = (formTypeId, subject, responseId, response, complete=false, status='incomplete', revision=null) => {
    const source = subject?.source ?? 'paua';
    const subjectId = subject?.id ?? null;
    const client_engagement_id = subject?.clientEngagementId ?? subject?.client_engagement_id ?? null;
    return axiosInstance.patch(`/app/dynamic-forms/${formTypeId}/responses/${subjectId}/${responseId}`,
        { response, complete, status, revision, source, client: legacyClientPayload(subject), client_engagement_id });
}

const getEngagementFormHistory = (clientEngagementId) =>
    axiosInstance.get(`/app/dynamic-forms/engagements/${clientEngagementId}/responses`, { cache: false }).then(res => res);

/**
 * Deletes an existing dynamic form response. Response IDs are globally unique,
 * so the subject segment is not significant server-side.
 */
const deleteDynamicForm = (formTypeId, responseId) => {
    return axiosInstance.delete(`/app/dynamic-forms/${formTypeId}/responses/0/${responseId}`);
}

const sendExternalFormInvitation = (formTypeId, subject, expiryDays = 30) =>
    axiosInstance.post(`/app/dynamic-forms/${formTypeId}/invitations`, {
        subject: {
            source: subject?.source,
            id: subject?.id,
            clientEngagementId: subject?.clientEngagementId ?? subject?.client_engagement_id ?? null,
        },
        expiryDays,
    });

const getExternalFormInvitation = (token) =>
    axiosInstance.get('/public/dynamic-form-invitations/current', {
        cache: false,
        headers: { 'X-Form-Token': token },
    });

const submitExternalFormInvitation = (token, response, formId) =>
    axiosInstance.post('/public/dynamic-form-invitations/current/responses', { response, formId }, {
        headers: { 'X-Form-Token': token },
    });

/**
 * Assign a list of response IDs from temporary clients to a specific client.
 */
const assignToClient = (client, responseIds) => {
    const clientId = client?.client_id ?? null;
    if (!clientId) throw new Error("Client ID is required to assign form responses");
    return axiosInstance.post(`/app/dynamic-forms/assign/${clientId}`, { responseIds });
}

// --------- Form Builder API --------- //

/**
 * Get all available field types with default options
 */
const getFieldTypes = () => axiosInstance.get("/app/form-builder/field-types").then(res => res);

/**
 * Create a new form definition
 */
const createFormDefinition = (formData) => axiosInstance.post("/app/form-builder/forms", formData);

/**
 * Update an existing form definition
 */
const updateFormDefinition = (formId, formData) => axiosInstance.put(`/app/form-builder/forms/${formId}`, formData);

/**
 * Delete a form definition
 */
const deleteFormDefinition = (formId) => axiosInstance.delete(`/app/form-builder/forms/${formId}`);


/**
 * Download a single form response as a PDF file. Response IDs are globally
 * unique, so the subject segment is not significant server-side.
 */
const exportFormResponsePdf = async (formTypeId, responseId, clientName) => {
    const response = await axiosInstance({
        url: `/app/dynamic-forms/${formTypeId}/responses/0/${responseId}/pdf`,
        method: "GET",
        responseType: "blob",
        cache: false,
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `form_response_${clientName || 'unknown'}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

const getTriageReferralConfig = () =>
    axiosInstance.get("/app/triage-referral/config", { cache: false }).then(res => res);

const getTriageReferralServices = () =>
    axiosInstance.get("/app/triage-referral/services", { cache: false }).then(res => res);

const getTriageReferralResponses = (formKey) =>
    axiosInstance.get(`/app/triage-referral/${formKey}/responses`, { cache: false }).then(res => res);

/**
 * Download the XLSX bulk-entry template for a dynamic form type. Triggers a
 * browser download of the .xlsx file.
 * @param {number|string} formTypeId
 * @param {string} [formTitle] used for the download filename
 */
const downloadXlsxTemplate = async (formTypeId, formTitle = "form") => {
    const response = await axiosInstance({
        url: `/app/dynamic-forms/${formTypeId}/xlsx-template`,
        method: "GET",
        responseType: "blob",
        cache: false,
    });
    const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const safeName = String(formTitle).replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, "_") || "form";
    link.setAttribute("download", `${safeName}_Template.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Upload and synchronously import an XLSX file as anonymous responses.
 * @param {number|string} formTypeId
 * @param {File} file the .xlsx file selected by the user
 * @returns server import summary { imported, skipped, failed, ids, errors }
 */
const importXlsxResponses = (formTypeId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axiosInstance.post(
        `/app/dynamic-forms/${formTypeId}/xlsx/import`,
        formData,
        { cache: false, timeout: 300000 }
    );
};

const approveFormResponse = (formId, responseId, revision) => axiosInstance.post(`/app/dynamic-forms/${formId}/responses/${responseId}/approve`, { revision });
const changeFormVersion = (formId, action) => axiosInstance.post(`/app/form-builder/forms/${formId}/versions/${action}`);

export default {
    approveFormResponse,
    changeFormVersion,
    // Teiti Kahurangi Form APIs
    getTeitiKahurangiFormById,
    getTeItiKahurangiFormDataCharts,
    getTeitiKahurangiFormDataHistory,
    getTeitiKahurangiFormDataxportCSV,
    teitiKahurangiFormPost,

    // Feedback & Case Closure
    uploadFeedbackResponse,
    updateCaseClosureFormData,

    // Dynamic Form APIs
    getFormHistory,
    getPastFormResponse,
    getDynamicFormTypes,
    getDynamicForm,
    getKoTatouResponses,
    getEngagementFormHistory,
    submitDynamicForm,
    updateDynamicForm,
    deleteDynamicForm,
    assignToClient,
    sendExternalFormInvitation,
    getExternalFormInvitation,
    submitExternalFormInvitation,

    // Form Builder APIs
    getFieldTypes,
    createFormDefinition,
    updateFormDefinition,
    deleteFormDefinition,

    // Export APIs
    exportFormResponsePdf,

    // Triage and Referral
    getTriageReferralConfig,
    getTriageReferralServices,
    getTriageReferralResponses,
    
    // XLSX bulk-response APIs
    downloadXlsxTemplate,
    importXlsxResponses,
};

